import { apiService } from '@clarix/api-client';
import { pmsBase } from '../lib/base';
import { PMS_ENDPOINTS } from '../endpoints';
import type {
  EvaluationDetail,
  RequestOtpResult,
  SaveAnswersPayload,
  SubmitEvaluationResult,
  TeamEvaluationListParams,
  TeamEvaluationRow,
} from '../types';

const base = () => `${pmsBase()}${PMS_ENDPOINTS.TEAM_EVALUATIONS}`;

/**
 * Team (RM / observer) evaluations client (§9 of the frozen contract, M5).
 *
 * The queue is scoped to the signed-in user by the server — there is no
 * evaluator parameter to pass, and adding one would be a way to read another
 * manager's reportees.
 *
 * The four participant-scoped paths (`/:participantId`, `/answers`,
 * `/request-otp`, `/submit`) are declared so `pnpm pms:contract` can see them,
 * but the backend does not serve them yet: they need the T4 Submission table and
 * the OTP store, which are shared with M4's self-evaluation surface.
 */
export const teamApi = {
  /** FR-401 — the evaluator queue across every published run. */
  list: (params?: TeamEvaluationListParams) => {
    const qs = new URLSearchParams();
    if (params?.stage) qs.set('stage', params.stage);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return apiService.get<TeamEvaluationRow[]>(query ? `${base()}?${query}` : base());
  },

  /** FR-402/403 — the frozen form, this RM's draft, and the blind-gated counterpart. */
  get: (participantId: string) =>
    apiService.get<EvaluationDetail>(`${base()}/${participantId}`),

  /** Autosave. Partial — only the questions named are written. */
  saveAnswers: (participantId: string, data: SaveAnswersPayload) =>
    apiService.post<EvaluationDetail>(`${base()}/${participantId}/answers`, data),

  /** PX-07 — mail a confirmation code. Refused unless the form is complete. */
  requestOtp: (participantId: string) =>
    apiService.post<RequestOtpResult>(`${base()}/${participantId}/request-otp`, {}),

  /**
   * FR-403 — verify the code and lock this side.
   *
   * `dpdp_consent` is required by the API (D-34) — omitting it is a 400, not a
   * submission without consent.
   */
  submit: (participantId: string, otp: string, dpdpConsent: boolean) =>
    apiService.post<SubmitEvaluationResult>(`${base()}/${participantId}/submit`, {
      otp,
      dpdp_consent: dpdpConsent,
    }),
};
